import React from 'react';

interface ForTeamsSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromBlue: string;
            toPurple: string;
        };
    };
}

const ForTeamsSection: React.FC<ForTeamsSectionProps> = ({ highlightColor }) => {
    const features = [
        {
            title: 'Seamless Data Integration for Teams',
            desc: 'Easily add your players\' wearable, testing, and performance data.',
            image: '/images/home-valueprops-team-performance.jpg'
        },
        {
            title: 'Automated Workflows',
            desc: 'Automate reports and readiness alerts that fit your team’s training schedule.',
            image: '/images/home-valueprops-data-integration.jpg'
        },
        {
            title: 'Next-level Data Exploration',
            desc: ' Our performance layer adapts to your organization\'s unique suite of wearables and testing data. Your data. your systems. your insights.',
            image: '/images/home-valueprops-analytics.jpg'
        }
    ];

    return (
        <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 bg-black relative">
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="max-w-6xl mx-auto relative z-10 text-center pb-12 border-b border-neutral-800">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 observe-fade opacity-0">
                        Performance Solutions for Every Team
                    </h1>
                    <p className="text-gray-300 max-w-3xl mx-auto observe-fade opacity-0">
                        From collegiate athletics to professional sports, CGM Sports provides intelligent performance solutions tailored to your team's needs.
                    </p>
                </div>
                <div className="mt-12 mb-12 observe-fade opacity-0 text-center">
                    <div className="flex items-center justify-center">
                        <img
                            src={'/images/home-partnership-dashboard.png'}
                            className="w-auto h-32 object-cover"
                        />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Sports Programs</h2>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                        <span 
                            className="bg-clip-text text-transparent"
                            style={{
                                backgroundImage: `linear-gradient(to right, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            Power Your Team's Edge
                        </span>
                    </h3>
                    <h4 className="text-xl md:text-2xl font-semibold text-gray-300 mb-6">
                        Your Intelligent Performance Layer
                    </h4>
                    <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
                        Our AI-powered platform connects data from multiple wearable devices and learns from your team's performance patterns. Get intelligent insights that help coaches optimize training and keep athletes performing at their best.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                    {features.map((feature, index) => (
                        <div key={index} className="bg-neutral-900 rounded-xl overflow-hidden observe-fade opacity-0 border border-neutral-800 text-center" style={{ animationDelay: `${index * 0.2}s` }}>
                            <img
                                src={feature.image}
                                alt={feature.title}
                                className="w-full h-48 object-cover"
                            />
                            <div className="p-6">
                                <h6 className="text-lg font-bold text-white mb-2">{feature.title}</h6>
                                <p className="text-gray-300">{feature.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ForTeamsSection;

